'use client';

import { useQuery } from '@tanstack/react-query';
import type { BrowserRecordingUrlResponseDto } from '@terra/shared-contracts-artifact';
import { artifactClient } from '@terra/shared-rest-clients-artifact';

export const BROWSER_RECORDING_URL_QUERY_KEY = 'query.browser-recording-url';

const RECORDING_UPLOAD_WINDOW_MS = 2 * 60 * 1000;
const PENDING_RECORDING_REFETCH_MS = 5_000;
const SIGNED_URL_STALE_TIME_MS = 10 * 60 * 1000;

type UseBrowserRecordingUrlParams = {
  artifactId: string | undefined;
  artifactCreatedAt: Date | undefined;
};

const fetchBrowserRecordingUrl = async (
  artifactId: string,
): Promise<BrowserRecordingUrlResponseDto | null> => {
  const response = await artifactClient.getArtifactRecordingUrl({
    params: { artifactId },
  });

  if (response.status === 200) {
    return response.body;
  }

  if (response.status === 404) {
    return null;
  }

  throw new Error('Failed to load browser recording URL');
};

const isWithinUploadWindow = (artifactCreatedAt: Date | undefined): boolean =>
  !!artifactCreatedAt &&
  Date.now() - new Date(artifactCreatedAt).getTime() <
    RECORDING_UPLOAD_WINDOW_MS;

export const useBrowserRecordingUrl = ({
  artifactId,
  artifactCreatedAt,
}: UseBrowserRecordingUrlParams) => {
  const queryResult = useQuery({
    queryKey: [BROWSER_RECORDING_URL_QUERY_KEY, artifactId],
    queryFn: () =>
      artifactId ? fetchBrowserRecordingUrl(artifactId) : Promise.resolve(null),
    enabled: !!artifactId,
    staleTime: SIGNED_URL_STALE_TIME_MS,
    refetchOnWindowFocus: false,
    refetchInterval: (query) =>
      query.state.data === null && isWithinUploadWindow(artifactCreatedAt)
        ? PENDING_RECORDING_REFETCH_MS
        : false,
  });

  return {
    recordingUrl: queryResult.data ?? null,
    isLoading: queryResult.isLoading,
    error: queryResult.error,
  };
};
